/**
 * Dates and times, always in the studio's zone. The founder works in IST and
 * so does every "today", "due" and "tomorrow at 10" in Desk, whatever zone the
 * browser happens to be in.
 */

export const TZ = 'Asia/Kolkata';

const dayFmt = new Intl.DateTimeFormat('en-IN', { timeZone: TZ, day: 'numeric', month: 'short' });
const dayYearFmt = new Intl.DateTimeFormat('en-IN', { timeZone: TZ, day: 'numeric', month: 'short', year: 'numeric' });
const timeFmt = new Intl.DateTimeFormat('en-IN', { timeZone: TZ, hour: 'numeric', minute: '2-digit', hour12: true });
const weekdayFmt = new Intl.DateTimeFormat('en-IN', { timeZone: TZ, weekday: 'short', day: 'numeric', month: 'short' });
const ymdFmt = new Intl.DateTimeFormat('en-CA', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit' });

/** YYYY-MM-DD of an instant, in IST. */
export const istDay = (d: Date | string) => ymdFmt.format(new Date(d));

/** "3m", "2h", "4d", "12 Oct": how long ago, as tight as a list column allows. */
export function ago(iso: string, now = Date.now()) {
  const s = Math.max(0, (now - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'now';
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86_400) return `${Math.floor(s / 3600)}h`;
  if (s < 7 * 86_400) return `${Math.floor(s / 86_400)}d`;
  const d = new Date(iso);
  return new Date().getFullYear() === d.getFullYear() ? dayFmt.format(d) : dayYearFmt.format(d);
}

/** "Today, 4:10 pm" / "Yesterday, 9:02 am" / "Thu 9 Oct, 11:30 am". */
export function when(iso: string) {
  const d = new Date(iso);
  const today = istDay(new Date());
  const yesterday = istDay(new Date(Date.now() - 86_400_000));
  const tomorrow = istDay(new Date(Date.now() + 86_400_000));
  const day = istDay(d);
  const label =
    day === today ? 'Today' : day === yesterday ? 'Yesterday' : day === tomorrow ? 'Tomorrow' : weekdayFmt.format(d);
  return `${label}, ${timeFmt.format(d)}`;
}

/** A plain calendar date (YYYY-MM-DD, as the form stores it): never shifted by a zone. */
export function calendarDate(ymd: string) {
  const d = new Date(`${ymd}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return ymd;
  return new Intl.DateTimeFormat('en-IN', { timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short' }).format(d);
}

/** Their local clock right now, from an IANA zone the form captured. */
export function theirTime(timezone: string | null | undefined) {
  if (!timezone || timezone === TZ) return null;
  try {
    return new Intl.DateTimeFormat('en-IN', { timeZone: timezone, hour: 'numeric', minute: '2-digit', hour12: true }).format(
      new Date(),
    );
  } catch {
    return null;
  }
}

/** An IST wall-clock time on an IST day, as an instant. IST has no DST: +05:30 always. */
export function istAt(ymd: string, hhmm: string) {
  return new Date(`${ymd}T${hhmm}:00+05:30`).toISOString();
}

/** Quick follow-up choices, all at 10 am IST so they land at the start of a working day. */
export function followUpPresets() {
  const day = (n: number) => istDay(new Date(Date.now() + n * 86_400_000));
  return [
    { label: 'Tomorrow', at: istAt(day(1), '10:00') },
    { label: 'In 3 days', at: istAt(day(3), '10:00') },
    { label: 'Next week', at: istAt(day(7), '10:00') },
  ];
}

/** For <input type="datetime-local">, in IST. */
export function toLocalInput(iso: string | null) {
  if (!iso) return '';
  const d = new Date(iso);
  const t = new Intl.DateTimeFormat('en-GB', { timeZone: TZ, hour: '2-digit', minute: '2-digit', hour12: false }).format(d);
  return `${istDay(d)}T${t}`;
}
export function fromLocalInput(v: string) {
  if (!v) return null;
  const [ymd, hm] = v.split('T');
  return istAt(ymd, hm.slice(0, 5));
}
